import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  CreateProactiveTriggerRequest,
  ProactiveTrigger,
  ProactiveTriggerResponse,
} from './types';

const proactiveTriggerApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createProactiveTrigger: builder.mutation<
      ProactiveTrigger,
      CreateProactiveTriggerRequest
    >({
      invalidatesTags: ['ProactiveTriggers'],
      async onQueryStarted(
        { events_to_trigger, name },
        { dispatch, queryFulfilled },
      ) {
        // optimistic update
        const patchResult = dispatch(
          proactiveTriggerApi.util.updateQueryData(
            'getProactiveTriggers',
            undefined,
            draft => {
              draft.proactive_triggers.push({
                events_to_trigger,
                name,
                proactive_trigger_id: 'temp-id',
              });
            },
          ),
        );

        try {
          const { data: created } = await queryFulfilled;
          // replace temp entry with server entry
          dispatch(
            proactiveTriggerApi.util.updateQueryData(
              'getProactiveTriggers',
              undefined,
              draft => {
                const idx = draft.proactive_triggers.findIndex(
                  t => t.proactive_trigger_id === 'temp-id',
                );
                if (idx !== -1) {
                  draft.proactive_triggers[idx] = created;
                }
              },
            ),
          );
        } catch {
          // rollback on error
          patchResult.undo();
        }
      },
      query: ({ events_to_trigger, name }) => ({
        body: {
          events_to_trigger,
          name,
        },
        method: 'POST',
        url: SOLVE_V2_BASE_URL + '/proactive-triggers',
      }),
    }),
    deleteProactiveTrigger: builder.mutation<void, string>({
      invalidatesTags: ['ProactiveTriggers'],
      query: id => ({
        method: 'DELETE',
        url: SOLVE_V2_BASE_URL + `/proactive-triggers/${id}`,
      }),
    }),

    getProactiveTriggers: builder.query<ProactiveTriggerResponse, void>({
      providesTags: ['ProactiveTriggers'],
      query: () => {
        return SOLVE_V2_BASE_URL + '/proactive-triggers';
      },
    }),
    updateProactiveTrigger: builder.mutation<
      ProactiveTrigger,
      ProactiveTrigger
    >({
      invalidatesTags: ['ProactiveTriggers'],
      async onQueryStarted(updatedTrigger, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          proactiveTriggerApi.util.updateQueryData(
            'getProactiveTriggers',
            undefined,
            draft => {
              const idx = draft.proactive_triggers.findIndex(
                t =>
                  t.proactive_trigger_id ===
                  updatedTrigger.proactive_trigger_id,
              );
              if (idx !== -1) {
                draft.proactive_triggers[idx].events_to_trigger =
                  updatedTrigger.events_to_trigger;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ events_to_trigger, name, proactive_trigger_id }) => ({
        body: {
          events_to_trigger,
          name,
        },
        method: 'PUT',
        url: SOLVE_V2_BASE_URL + `/proactive-triggers/${proactive_trigger_id}`,
      }),
    }),
  }),
});

export const {
  useCreateProactiveTriggerMutation,
  useDeleteProactiveTriggerMutation,
  useGetProactiveTriggersQuery,
  useUpdateProactiveTriggerMutation,
} = proactiveTriggerApi;
